import React, { useState, useEffect } from 'react';
import { useCreateQuizMutation, useAddQuestionToQuizMutation } from '../redux/apiSlice';

const QuizComponent = ({ videoId, videoTitle, showToast, existingQuizzes = [] }) => {
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [quizzes, setQuizzes] = useState(existingQuizzes); // Store multiple quizzes from props
  const [currentQuizIndex, setCurrentQuizIndex] = useState(null);
  
  // New quiz form state
  const [quizTitle, setQuizTitle] = useState(`Quiz for ${videoTitle}`);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentOptions, setCurrentOptions] = useState([
    { text: '', isCorrect: true },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false }
  ]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdQuizId, setCreatedQuizId] = useState(null);

  const [createQuiz] = useCreateQuizMutation();
  const [addQuestionToQuiz] = useAddQuestionToQuizMutation();

  // Debug: Log quizzes when component mounts or updates
  useEffect(() => {
    console.log('📋 QuizComponent - Received existingQuizzes:', existingQuizzes);
    console.log('📋 QuizComponent - Quizzes state:', quizzes);
  }, [existingQuizzes, quizzes]);

  // Reset form for new quiz
  const resetQuizForm = () => {
    setQuizTitle(`Quiz for ${videoTitle}`);
    setQuestions([]);
    setCurrentQuestion('');
    setCurrentOptions([
      { text: '', isCorrect: true },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]);
  };

  // Start creating new quiz
  const handleStartNewQuiz = () => {
    resetQuizForm();
    setCurrentQuizIndex(null);
    setShowQuizForm(true);
  };

  // Edit existing quiz
  const handleEditQuiz = (quizIndex) => {
    const quiz = quizzes[quizIndex];
    setQuizTitle(quiz.title);
    setQuestions(quiz.questions || []);
    setCurrentQuizIndex(quizIndex);
    setShowQuizForm(true);
  };

  // Delete quiz
  const handleDeleteQuiz = (quizIndex) => {
    if (window.confirm('Are you sure you want to delete this quiz?')) {
      const updatedQuizzes = quizzes.filter((_, index) => index !== quizIndex);
      setQuizzes(updatedQuizzes);
      showToast('Quiz deleted successfully', 'success');
    }
  };

  const handleAddQuestion = () => {
    if (!currentQuestion.trim()) {
      showToast('Please enter a question', 'error');
      return;
    }

    // Validate options
    const validOptions = currentOptions.filter(option => option.text.trim());
    if (validOptions.length < 2) {
      showToast('Please add at least 2 options', 'error');
      return;
    }

    // Check if exactly one option is correct
    const correctOptions = validOptions.filter(option => option.isCorrect);
    if (correctOptions.length !== 1) {
      showToast('Please mark exactly one option as correct', 'error');
      return;
    }

    const newQuestion = {
      id: Date.now(),
      question: currentQuestion,
      options: [...validOptions]
    };

    setQuestions([...questions, newQuestion]);
    setCurrentQuestion('');
    setCurrentOptions([
      { text: '', isCorrect: true },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]);

    showToast('Question added successfully', 'success');
  };

  const handleRemoveQuestion = (index) => {
    const updatedQuestions = [...questions];
    updatedQuestions.splice(index, 1);
    setQuestions(updatedQuestions);
    showToast('Question removed', 'info');
  };

  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...currentOptions];
    
    if (field === 'isCorrect') {
      // Only one option can be correct
      updatedOptions.forEach((option, i) => {
        option.isCorrect = i === index;
      });
    } else {
      updatedOptions[index][field] = value;
    }
    
    setCurrentOptions(updatedOptions);
  };

  const handleCreateQuiz = async () => {
    if (!quizTitle.trim()) {
      showToast('Please enter a quiz title', 'error');
      return;
    }

    if (questions.length === 0) {
      showToast('Please add at least one question', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // If editing existing quiz that was already saved
      if (currentQuizIndex !== null && quizzes[currentQuizIndex]?.id) {
        // Update local state only (for now)
        const updatedQuizzes = [...quizzes];
        updatedQuizzes[currentQuizIndex] = {
          ...updatedQuizzes[currentQuizIndex],
          title: quizTitle,
          questions: questions,
          totalQuestions: questions.length
        };
        setQuizzes(updatedQuizzes);
        showToast('🎉 Quiz updated successfully!', 'success');
        setShowQuizForm(false);
        setIsSubmitting(false);
        return;
      }

      // Step 1: Create the quiz
      const quizResult = await createQuiz({
        title: quizTitle,
        videoId: videoId
      }).unwrap();

      console.log('✅ Quiz created:', quizResult);
      setCreatedQuizId(quizResult.quiz.id);

      // Step 2: Add all questions to the quiz
      for (const question of questions) {
        await addQuestionToQuiz({
          quizId: quizResult.quiz.id,
          question: question.question,
          options: question.options
        }).unwrap();
      }

      // Add new quiz to quizzes list
      const newQuiz = {
        id: quizResult.quiz.id,
        title: quizResult.quiz.title,
        questions: questions,
        totalQuestions: questions.length,
        createdAt: new Date().toISOString()
      };

      setQuizzes([...quizzes, newQuiz]);
      showToast('🎉 Quiz created successfully!', 'success');
      setShowQuizForm(false);
      
    } catch (error) {
      console.error('❌ Failed to create quiz:', error);
      
      let errorMessage = 'Failed to create quiz. Please try again.';
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to render questions preview with scroll bar
  const renderQuestionsPreview = (questions) => {
    if (!questions || questions.length === 0) return null;
    
    return (
      <div className="mt-3 border-t border-gray-100 pt-3">
        <div className="flex justify-between items-center mb-2">
          <p className="text-xs font-medium text-gray-500">
            Questions Preview ({questions.length} total)
          </p>
          <span className="text-xs text-gray-400">
            Scroll to see all questions ↓
          </span>
        </div>
        <div className="max-h-60 overflow-y-auto pr-2 space-y-2 custom-scrollbar">
          {questions.map((q, idx) => (
            <div key={idx} className="text-sm bg-gray-50 p-3 rounded border border-gray-200">
              <div className="flex items-start">
                <span className="text-gray-500 font-medium mr-2 min-w-[20px]">{idx + 1}.</span>
                <div className="flex-1">
                  <span className="text-gray-800 font-medium block mb-2">{q.question || q.text}</span>
                  <div className="ml-2 space-y-1.5">
                    {(q.options || []).map((opt, optIdx) => (
                      <div key={optIdx} className="flex items-center text-xs">
                        <span className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${opt.isCorrect ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        <span className={opt.isCorrect ? 'text-green-600 font-medium' : 'text-gray-600'}>
                          {opt.text} {opt.isCorrect && '✓'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Scroll bar height indicator */}
        {questions.length > 3 && (
          <div className="mt-2 text-xs text-gray-400 text-right">
            Showing all {questions.length} questions
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-4">
      {/* Add Quiz Button */}
      <button
        type="button"
        onClick={handleStartNewQuiz}
        className="px-4 py-2 bg-purple-500 text-white rounded-lg font-medium text-sm hover:bg-purple-600 transition-colors duration-300 flex items-center space-x-2"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <span>{showQuizForm ? 'Hide Quiz Form' : 'Add Quiz to Video'}</span>
      </button>

      {/* Display all quizzes */}
      {quizzes.length > 0 && !showQuizForm && (
        <div className="space-y-4 mt-4">
          {quizzes.map((quiz, quizIndex) => (
            <div key={quizIndex} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1 flex items-center">
                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded mr-2">
                      Quiz #{quizIndex + 1}
                    </span>
                    {quiz.title}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {quiz.totalQuestions || quiz.questions?.length || 0} Questions
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* Edit button */}
                  <button
                    onClick={() => handleEditQuiz(quizIndex)}
                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                    title="Edit Quiz"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>

                  {/* Delete button */}
                  <button
                    onClick={() => handleDeleteQuiz(quizIndex)}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                    title="Delete Quiz"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Questions Preview with Scroll Bar */}
              {renderQuestionsPreview(quiz.questions)}
            </div>
          ))}
        </div>
      )}

      {/* Quiz Creation/Edit Form */}
      {showQuizForm && (
        <div className="mt-4 bg-purple-50 border border-purple-200 rounded-xl p-4">
          <h4 className="font-semibold text-purple-900 mb-3">
            {currentQuizIndex !== null ? 'Edit Quiz' : 'Create Quiz for Video'}
          </h4>
          
          {/* Quiz Title */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-purple-700 mb-1">
              Quiz Title *
            </label>
            <input
              type="text"
              value={quizTitle}
              onChange={(e) => setQuizTitle(e.target.value)}
              placeholder="Enter quiz title"
              className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          {/* Current Question Input */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-purple-700 mb-1">
              Question *
            </label>
            <input
              type="text"
              value={currentQuestion}
              onChange={(e) => setCurrentQuestion(e.target.value)}
              placeholder="Enter your question here"
              className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-3"
              required
            />

            {/* Options */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-purple-700 mb-1">
                Options (Mark one as correct) *
              </label>
              {currentOptions.map((option, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={option.isCorrect}
                    onChange={() => handleOptionChange(index, 'isCorrect', true)}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 px-3 py-2 text-sm border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {!option.text.trim() && (
                    <span className="text-xs text-gray-400">Required</span>
                  )}
                </div>
              ))}
            </div>

            {/* Add Question Button */}
            <button
              type="button"
              onClick={handleAddQuestion}
              className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-700 transition-colors duration-300"
            >
              Add This Question
            </button>
          </div>

          {/* Questions List Preview */}
          {questions.length > 0 && (
            <div className="mb-4">
              <h5 className="text-xs font-medium text-purple-700 mb-2">
                Added Questions ({questions.length})
              </h5>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {questions.map((q, index) => (
                  <div key={q.id} className="bg-white border border-purple-100 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{q.question}</p>
                        <div className="mt-1 space-y-1">
                          {q.options.map((opt, optIndex) => (
                            <div key={optIndex} className="flex items-center space-x-2">
                              <div className={`w-2 h-2 rounded-full ${opt.isCorrect ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                              <span className={`text-xs ${opt.isCorrect ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
                                {opt.text}
                                {opt.isCorrect && ' (Correct)'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveQuestion(index)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove question"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Create/Update Quiz Button */}
          <div className="flex justify-between items-center pt-4 border-t border-purple-200">
            <button
              type="button"
              onClick={() => {
                setShowQuizForm(false);
                setCurrentQuizIndex(null);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-300 text-sm"
            >
              Cancel
            </button>
            
            <button
              type="button"
              onClick={handleCreateQuiz}
              disabled={isSubmitting || questions.length === 0}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{currentQuizIndex !== null ? 'Updating...' : 'Creating Quiz...'}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{currentQuizIndex !== null ? `Update Quiz (${questions.length} questions)` : `Create Quiz (${questions.length} questions)`}</span>
                </>
              )}
            </button>
          </div>

          {/* Quiz Created Success Message */}
          {createdQuizId && !showQuizForm && (
            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2 text-green-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-xs font-medium">Quiz created successfully! Quiz ID: {createdQuizId}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No quizzes message */}
      {!showQuizForm && quizzes.length === 0 && (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <svg className="w-4 h-4 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-xs text-gray-600 mb-1">
                <span className="font-medium">Video Quizzes:</span> Add quizzes to test students after watching this video.
              </p>
              <p className="text-xs text-gray-500">
                Click "Add Quiz to Video" to create your first quiz.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default QuizComponent;